import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const SECTION_SELECTOR = '[data-agency-moment]';
const instances = new Map();
const prefersReducedMotionQuery = window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null;


  /* ----------------------------------------------
   LIGHTING MODES — comment/uncomment to test
   ---------------------------------------------- */

  // MODE 1 — SUBTLE EMBOSSED (clean, premium)
  // const LIGHT_MODE = {
  //   BASE_Y: 6,
  //   DRIFT_Y: 1.8,
  //   BASE_Z: -6,
  //   DRIFT_X: 2.2,
  //   DRIFT_Z: 2.2
  // };

  // MODE 2 — PREMIUM INTERACTIVE (strong but controlled)
  // const LIGHT_MODE = {
  //   BASE_Y: 6.4,
  //   DRIFT_Y: 3,
  //   BASE_Z: -8,
  //   DRIFT_X: 4,
  //   DRIFT_Z: 4
  // };

  // MODE 3 — CINEMATIC (long sweeping dramatic shadows)
const MOBILE_MAX_WIDTH = 768;

const LIGHT_MODE = {
  BASE_Y: 6.5,   // height above the model — stable, not dramatic
  DRIFT_Y: 4.5,  // vertical drift
  BASE_Z: 0.5,   // ***small forward bias gives room on BOTH sides***
  DRIFT_Z: 10,   // ***full symmetric shadow control***
  DRIFT_X: 7     // horizontal drift
};

  // MODE 4 — EXTREME (very responsive, high movement, bold shadows)
  // const LIGHT_MODE = {
  //   BASE_Y: 7,
  //   DRIFT_Y: 8,
  //   BASE_Z: -12,
  //   DRIFT_X: 8,
  //   DRIFT_Z: 8
  // };


class AgencyMoment {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.mediaEl = section.querySelector('[data-moment-media]');
    this.stageEl = section.querySelector('[data-moment-stage]');
    this.mediaType = section.dataset.mediaType;
    this.glbSrc = this.mediaEl?.dataset.glbSrc;
    this.frame = null;
    this.isRunning = false;

    if (!this.mediaEl || !this.stageEl) return;
    if (this.mediaType !== 'glb' || !this.glbSrc) {
      this.showFallback();
      return;
    }

    this.handleMotionChange = () => this.togglePointerTracking();
    this.lastPointerEvent = null;
    this.scrollUpdateScheduled = false;
    this.onMouseMove = (event) => {
      this.lastPointerEvent = {
        clientX: event.clientX,
        clientY: event.clientY
      };
      this.updateLightPosition(event);
    };
    this.onTouchMove = (event) => {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      if (touch) {
        this.lastPointerEvent = {
          clientX: touch.clientX,
          clientY: touch.clientY
        };
        this.updateLightPosition(touch);
      }
    };
    this.onScroll = () => this.scheduleScrollUpdate();
    this.onResize = () => this.handleResize();
    this.prefersReducedMotion = prefersReducedMotionQuery;

    this.initThree();
  }


  initThree() {
  try {
    // ——————————————————————————————————————————
    // Canvas Setup — wrapper for the WebGL renderer
    // ——————————————————————————————————————————
    this.canvasWrapper = document.createElement('div');
    this.canvasWrapper.className = 'moment__canvas';
    this.mediaEl.appendChild(this.canvasWrapper);

    // ——————————————————————————————————————————
    // SCENE
    // ——————————————————————————————————————————
    this.scene = new THREE.Scene();

    // ——————————————————————————————————————————
    // CAMERA — true top-down camera
    // ——————————————————————————————————————————
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);

    // Position camera directly above the model.
    // y = height, x/z = lateral offsets.
    const { width: stageWidth } = this.getStageSize();
    this.updateCameraHeight(stageWidth);

    // Point straight down at the model and shadow plane.
    this.camera.lookAt(0, 0, 0);

    // ——————————————————————————————————————————
    // RENDERER — high-quality shadows + exposure
    // ——————————————————————————————————————————
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Turn on soft shadows
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Use filmic tone mapping for softer highlights
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;

    // Brighter exposure to stop the model from appearing gray
    this.renderer.toneMappingExposure = 2.35;

    this.canvasWrapper.appendChild(this.renderer.domElement);

    // Light tint of the background (matches your brand's off-white tone)
    const groundColor = new THREE.Color(0xf1f1ec);
    this.renderer.setClearColor(groundColor, 1);

    // ——————————————————————————————————————————
    // LIGHTS
    // ——————————————————————————————————————————

    // Ambient light provides general brightness so the model isn't harshly contrasted.
    // Increasing this makes the whole scene brighter without killing shadows.
    const ambient = new THREE.AmbientLight(0xffffff, 0.55); //higher is brigher and less contrast
    this.scene.add(ambient);

    // Directional key light controls shadow direction and length.
    // This position is responsible for the "embossed" look.
    //
    // x: light coming from the right
    // y: height of the light (lower = longer shadows)
    // z: forward/back tilt
    //
    // These values create shadows flowing horizontally like your reference image.
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
    this.directionalLight.castShadow = true;
    this.directionalLight.position.set(4, 6, -4);

    // Make sure the light actually points at the model
    this.directionalLight.target.position.set(0, 0, 0);
    this.directionalLight.target.updateMatrixWorld();
    this.scene.add(this.directionalLight);
    this.scene.add(this.directionalLight.target);

    // SHADOW QUALITY SETTINGS
    // Higher mapSize = sharper, softer-edged shadows
    this.directionalLight.shadow.mapSize.set(2048, 2048);

    // Bias settings prevent shadow acne and flickering
    this.directionalLight.shadow.bias = -0.0006;
    this.directionalLight.shadow.normalBias = 0.02;

    // Shadow camera extents — controls how far shadows project
    // Larger values = wider shadow coverage area
    this.directionalLight.shadow.camera.near = 0.1;
    this.directionalLight.shadow.camera.far = 20;
    this.directionalLight.shadow.camera.left = -8;
    this.directionalLight.shadow.camera.right = 8;
    this.directionalLight.shadow.camera.top = 8;
    this.directionalLight.shadow.camera.bottom = -8;
    

    // Subtle hemisphere fill light to smooth out overly dark areas
    const fill = new THREE.HemisphereLight(0xffffff, 0xdddddd, .25); // make over 1 to brighten - mb
    this.scene.add(fill);

    // ——————————————————————————————————————————
    // SHADOW RECEIVING PLANE
    // ——————————————————————————————————————————
    //
    // This plane sits directly under the model and receives the shadow.
    // ShadowMaterial draws ONLY the shadow, not the plane itself.
    //
    const shadowGeo = new THREE.PlaneGeometry(20, 20);

    // opacity determines how visible the shadow is on the ground
    const shadowMat = new THREE.ShadowMaterial({ opacity: .38 });

    const shadowPlane = new THREE.Mesh(shadowGeo, shadowMat);

    // Rotate plane so it lies flat in the XZ axis
    shadowPlane.rotation.x = -Math.PI / 2;

    // y = height of plane; 0 keeps the model sitting flush
    shadowPlane.position.y = 0;

    shadowPlane.receiveShadow = true;
    this.scene.add(shadowPlane);

    // ——————————————————————————————————————————
    // LOAD GLB MODEL
    // ——————————————————————————————————————————
    this.loader = new GLTFLoader();
    if (this.loader.setCrossOrigin) this.loader.setCrossOrigin('anonymous');

    this.loader.load(
      this.glbSrc,
      (gltf) => {
        this.model = gltf.scene;

        // GLB models often import upright.
        // Rotate -90° to lay flat on the plane.
        this.model.rotation.x = -Math.PI / 2;

        // Lift slightly above plane for clean shadow separation
        this.model.position.set(0, 0.01, 0);

        // Enable shadows on all meshes
        this.model.traverse((obj) => {
          if (obj.isMesh) {
            obj.castShadow = true;
            obj.receiveShadow = true;

            // Slight adjustments to roughness to keep the model matte
            if (obj.material) {
              obj.material.roughness = 0.85;
              obj.material.metalness = 0.0;
            }
          }
        });

        this.scene.add(this.model);
        this.start();
      },
      undefined,
      (error) => {
        console.error('[Agency Moment] Failed to load GLB:', error);
        this.showFallback();
        this.destroy();
      }
    );
  } catch (error) {
    console.error('[Agency Moment] Initialization error:', error);
    this.showFallback();
    this.destroy();
  }
}

  start() {
    this.handleResize();
    this.togglePointerTracking();
    this.prefersReducedMotion?.addEventListener?.('change', this.handleMotionChange);
    if (this.prefersReducedMotion && !this.prefersReducedMotion.addEventListener) {
      this.prefersReducedMotion.addListener(this.handleMotionChange);
    }
    window.addEventListener('resize', this.onResize);
    this.isRunning = true;
    const animate = () => {
      if (!this.isRunning) return;
      this.renderer.render(this.scene, this.camera);
      this.frame = requestAnimationFrame(animate);
    };
    animate();
  }

  togglePointerTracking() {
    const shouldTrack =
      !this.prefersReducedMotion || (this.prefersReducedMotion.matches === false);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('scroll', this.onScroll);
    if (shouldTrack) {
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('touchmove', this.onTouchMove);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }


  
  updateLightPosition(event) {
  if (!this.stageEl) return;

  const rect = this.stageEl.getBoundingClientRect();

  // Normalized mouse coords: -1 to +1
  const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const ny = -(((event.clientY - rect.top) / rect.height) * 2 - 1);

  // Extract mode values
  const {
    BASE_Y,
    DRIFT_Y,
    BASE_Z,
    DRIFT_X,
    DRIFT_Z
  } = LIGHT_MODE;

  // Light x responds symmetrically:
  // const lightX = nx * DRIFT_X; // move left = shadow right, move right = shadow left
  const lightX = -nx * DRIFT_X; // move left = shadow left, move right = shadow right

  // Light z also responds symmetrically:
  // pushing light forward/back evenly
  const lightZ = BASE_Z + (ny * DRIFT_Z);

  // Vertical Y drift — symmetric but clamped so shadows never blow out
  let lightY = BASE_Y + (ny * DRIFT_Y);
  lightY = Math.max(lightY, 2.0);   // stop insane shadows
  lightY = Math.min(lightY, 12.0);  // prevent over-brightness

  // Apply
  this.directionalLight.position.set(lightX, lightY, lightZ);

  // Always point at the model
  this.directionalLight.target.position.set(0, 0, 0);
}


  handleResize() {
    if (!this.stageEl || !this.renderer) return;
    const { width, height } = this.getStageSize();
    if (width === 0 || height === 0) return;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.updateCameraHeight(width);
  }

  getStageSize() {
    const rect = this.stageEl.getBoundingClientRect();
    return {
    width: rect.width || 600,
    height: rect.height || 400
  };
}

  scheduleScrollUpdate() {
    if (this.scrollUpdateScheduled) return;
    this.scrollUpdateScheduled = true;
    requestAnimationFrame(() => {
      this.scrollUpdateScheduled = false;
      const pointerEvent = this.lastPointerEvent || this.createCenterPointerEvent();
      if (pointerEvent) {
        this.updateLightPosition(pointerEvent);
      }
    });
  }

  createCenterPointerEvent() {
    if (!this.stageEl) return null;
    const rect = this.stageEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2
    };
  }

  updateCameraHeight(widthValue = 0) {
    if (!this.camera) return;
    const width = widthValue || window.innerWidth;
    const targetY = width <= MOBILE_MAX_WIDTH ? 10 : 6;
    if (this.camera.position.y === targetY) {
      return;
    }
    this.camera.position.y = targetY;
    this.camera.lookAt(0, 0, 0);
  }

  showFallback() {
    this.mediaEl?.classList.add('is-fallback-visible');
  }

  destroy() {
    this.isRunning = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('scroll', this.onScroll);
    if (this.prefersReducedMotion?.removeEventListener) {
      this.prefersReducedMotion.removeEventListener('change', this.handleMotionChange);
    } else if (this.prefersReducedMotion?.removeListener) {
      this.prefersReducedMotion.removeListener(this.handleMotionChange);
    }

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.domElement?.remove();
    }
    this.canvasWrapper?.remove();
    this.scene = null;
    this.renderer = null;
    this.camera = null;
    this.model = null;
  }
}

function initSection(section) {
  if (!section || section.dataset.mediaType !== 'glb') return;
  destroySection(section.dataset.sectionId);
  const instance = new AgencyMoment(section);
  if (instance.mediaType === 'glb' && instance.glbSrc) {
    instances.set(section.dataset.sectionId, instance);
  }
}

function destroySection(id) {
  const instance = instances.get(id);
  if (!instance) return;
  instance.destroy();
  instances.delete(id);
}

function initAll() {
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector(SECTION_SELECTOR);
  if (section) initSection(section);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroySection(event.detail.sectionId);
});
